import './LoginModal.scss';

import Cookies from 'js-cookie';
import { useCallback, useEffect } from 'react';
import { Actions } from '../../../actions/enums';
import { Wrapper } from '../standardModalWrapper';
import { useUnifiedAuth } from '../../../hooks/unifiedAuth/useUnifiedAuth';
import { LoginPage } from '../../Login/IcapsLoginModalContent';
import { CardInfo } from './LoyaltyCard/LoyaltyCard';
import { FailScreen } from './FailScreen/FailScreen';
import { MaintenanceMode } from './MaintenanceMode/MaintenanceMode';
import { getEnv } from '../../../utils/env';
import { isCiamEnabled } from '../../../utils/retailUnitsWithCiam';
import { CiamLoginModalContent } from '../../Login/CiamLoginModalContent';
import { MenuLoggedIn } from './Menu/Menu';
import { Application } from '@mvecom/common/enums/Application';
import { getAppName } from '../../../utils/getAppName';
import { useTranslation } from 'react-i18next';
import appMetadata from '@mvecom/common/utils/appMetadata';

const isMaintanceWindow = (() => {
  if (appMetadata.getRetailUnit() !== 'lv') {
    return false;
  }

  // Timewindow is in Sweden between 07:00 and 07:35.
  const startAt = new Date('2026-06-09T07:00:00+02:00');
  const endsAt = new Date('2026-06-09T07:35:00+02:00');

  const now = new Date();
  return now >= startAt && now <= endsAt;
})();

(globalThis.ikea.mvAgent as unknown as { maintenanceMode: boolean }).maintenanceMode = isMaintanceWindow;

export const LoginModal = () => {
  // Enabling the maintenance mode will display a different view
  // explaining to the user that login is currently unavailable
  const { t } = useTranslation('profile');

  const { authState, setAuthState, retryMenu, logout, icapsLogin } = useUnifiedAuth(isCiamEnabled ? 'ciam' : 'icaps');
  const close = useCallback(() => setAuthState('Closed'), [setAuthState]);

  // listen to pubsub messages for opening the menu
  useEffect(() => {
    const open = () => {
      if (globalThis.ikea.mvAgent.profilePagesFragmentLive || getAppName() === Application.PROFILE) {
        return;
      }

      setAuthState('Open');
    };

    const openLoginReminder = () => {
      if (Cookies.get('_session_id')) {
        globalThis.ikea.pubsub.publish(Actions.GO_TO_CHECKOUT);
      } else {
        setAuthState('Reminder');
      }
    };

    globalThis?.ikea?.pubsub?.subscribe(Actions.PROFILE_OPEN_MENU, open);
    globalThis?.ikea?.pubsub?.subscribe(Actions.PROFILE_OPEN_LOGIN_REMINDER, openLoginReminder);

    return () => {
      globalThis?.ikea?.pubsub?.unsubscribe(Actions.PROFILE_OPEN_MENU, open);
      globalThis?.ikea?.pubsub?.unsubscribe(Actions.PROFILE_OPEN_LOGIN_REMINDER, openLoginReminder);
    };
  }, [setAuthState]);

  const loyaltyCard = authState.status === 'Loyalty' ? authState.data : undefined;

  if (authState.status === 'Closed') {
    return <Wrapper visible={false} close={close} />;
  }

  if (authState.status === 'CheckingIfLoggedIn') {
    return <Wrapper visible loading close={close} />;
  }

  // for logged in states
  const searchParams = new URLSearchParams(globalThis.location.search);
  const showTestParam = searchParams.get('secretlyTestMaintenance') === 'true';
  const env = getEnv();

  if (isMaintanceWindow || (showTestParam && env !== 'production')) {
    return (
      <Wrapper visible close={close}>
        <MaintenanceMode dismiss={logout} />
      </Wrapper>
    );
  }

  if (
    authState.status === 'Unauthenticated' ||
    authState.status === 'Reminder' ||
    authState.status === 'ReminderMergeCheck'
  ) {
    if (isCiamEnabled) {
      // we don't do reminder modal for ciam
      return (
        <Wrapper ariaLabel={t('sideMenuLoggedOut.ariaLabel')} visible close={close}>
          <CiamLoginModalContent />
        </Wrapper>
      );
    }

    return (
      <Wrapper ariaLabel={t('sideMenuLoggedOut.ariaLabel')} visible close={close}>
        <LoginPage
          login={icapsLogin}
          loading={authState.status === 'ReminderMergeCheck' || authState.isLoggingIn}
          loginError={authState.status === 'ReminderMergeCheck' ? undefined : authState.error}
        />
      </Wrapper>
    );
  }

  if (authState.status === 'MenuLoading') {
    return <Wrapper loading visible close={close} />;
  }

  if (authState.status === 'MenuError') {
    return (
      <Wrapper visible close={close}>
        <FailScreen logout={logout} retry={retryMenu} />
      </Wrapper>
    );
  }

  if (authState.status === 'Menu') {
    return (
      <Wrapper noPadding visible close={close}>
        <MenuLoggedIn menu={authState.data} logout={logout} onLoyaltyCardClick={() => setAuthState('Loyalty')} />
      </Wrapper>
    );
  }

  if (authState.status === 'LogoutError' && authState.data) {
    return (
      <Wrapper noPadding visible close={close}>
        <MenuLoggedIn
          logoutError
          menu={authState.data}
          logout={logout}
          onLoyaltyCardClick={() => setAuthState('Loyalty')}
        />
      </Wrapper>
    );
  }

  if (authState.status === 'LogoutError') {
    return (
      <Wrapper noPadding visible close={close}>
        <FailScreen logout={logout} retry={retryMenu} />
      </Wrapper>
    );
  }

  if (authState.status === 'Loyalty') {
    return (
      <Wrapper
        title={authState.data.title}
        noPadding
        visible
        close={close}
        back={loyaltyCard ? () => setAuthState('Open') : undefined}
      >
        {authState.status === 'Loyalty' && <CardInfo loyaltyCard={authState.data} />}
      </Wrapper>
    );
  }

  authState satisfies never;
};
